import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, Loader2, ScanFace, Trash2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = () => ({ withCredentials: true });
const FACE_DEVICE_KEY = 'jmepl_face_device_ready';

let faceapi = null;
let modelsPromise = null;
const loadModels = () => {
  if (!modelsPromise) {
    modelsPromise = import('@vladmandic/face-api').then(async (mod) => {
      faceapi = mod;
      const tf = faceapi.tf;
      let ok = false;
      for (const b of ['webgl', 'cpu']) {
        try { ok = await tf.setBackend(b); if (ok) break; } catch (e) { ok = false; }
      }
      await tf.ready();
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
        faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
        faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
      ]);
      return faceapi;
    }).catch((e) => { modelsPromise = null; throw e; });
  }
  return modelsPromise;
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const ear = (p) => (dist(p[1], p[5]) + dist(p[2], p[4])) / (2 * dist(p[0], p[3]));

export const cameraSupported = () => !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
export const friendlyCamError = (e) => {
  if (e?.name === 'NotAllowedError') return 'Camera permission was denied. Allow camera access for this site and try again.';
  if (e?.name === 'NotFoundError') return 'No camera found on this device.';
  if (e?.name === 'NotReadableError') return 'The camera is being used by another app.';
  return e?.response?.data?.detail || e?.message || 'Camera / face scan failed';
};

/**
 * Live camera + face scan. Calls onCapture({descriptors, liveness}) once `samples` descriptors are
 * collected AND a blink was seen. status: 'loading' | 'ready' | 'noface' | 'blink' | 'done' | 'error'
 */
export const FaceCamera = ({ samples = 1, onCapture, onError, active = true, testid = 'face-camera' }) => {
  const video = useRef(null);
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(0);
  const [hint, setHint] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    let stream;
    const run = async () => {
      try {
        await loadModels();
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        video.current.srcObject = stream;
        try { await video.current.play(); } catch (e) { if (e?.name !== 'AbortError') throw e; }
        if (!alive) return;
        setStatus('ready');
        const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
        const collected = [];
        let closed = false, blinked = false, lastSample = 0, misses = 0, openEar = 0, noseMin = 1, noseMax = 0;
        const started = Date.now();
        while (alive && Date.now() - started < 60000) {
          await new Promise((r) => setTimeout(r, 120));
          if (!alive) return;
          if (!video.current || video.current.readyState < 2) continue;
          const r = await faceapi.detectSingleFace(video.current, opts).withFaceLandmarks().withFaceDescriptor();
          if (!alive) return;
          if (!r) { if (++misses > 6) setStatus('noface'); continue; }
          misses = 0;
          const e = (ear(r.landmarks.getLeftEye()) + ear(r.landmarks.getRightEye())) / 2;
          window.__faceEar = e;
          // Liveness = blink (eyes close then re-open) OR a small head turn (nose moves across the face box)
          openEar = Math.max(openEar * 0.98, e);
          if (e < 0.21 || (openEar > 0.2 && e < openEar * 0.72)) closed = true;
          if (closed && e > Math.max(0.24, openEar * 0.88)) blinked = true;
          const nose = r.landmarks.getNose()[3];
          const ratio = (nose.x - r.detection.box.x) / r.detection.box.width;
          noseMin = Math.min(noseMin, ratio); noseMax = Math.max(noseMax, ratio);
          if (noseMax - noseMin > 0.14) blinked = true;
          if (collected.length >= samples && !blinked && Date.now() - started > 8000) setHint(true);
          if (collected.length < samples && Date.now() - lastSample > 500) {
            collected.push(Array.from(r.descriptor));
            lastSample = Date.now();
            setProgress(collected.length);
          }
          setStatus(collected.length >= samples && !blinked ? 'blink' : 'ready');
          if (collected.length >= samples && blinked) {
            setStatus('done');
            alive = false;
            onCapture({ descriptors: collected, liveness: true });
            return;
          }
        }
        if (alive) { setStatus('error'); onError?.(new Error('Face scan timed out — please try again')); }
      } catch (e) {
        if (alive) { setStatus('error'); onError?.(e); }
      }
    };
    run();
    return () => { alive = false; stream?.getTracks().forEach((t) => t.stop()); };
  }, [active, samples, onCapture, onError]);

  const msg = {
    loading: 'Loading face scanner…', ready: samples > 1 ? `Hold still — capturing ${progress}/${samples}` : 'Look straight at the camera',
    noface: 'No face detected — move closer and face the camera', blink: hint ? 'Blink, or turn your head slightly left and right' : 'Now blink once', done: 'Face captured', error: 'Scan stopped',
  }[status];

  return (
    <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]" data-testid={testid}>
      <video ref={video} muted playsInline className="w-full h-full object-cover -scale-x-100" />
      {status === 'loading' && <div className="absolute inset-0 flex items-center justify-center text-white/80"><Loader2 className="w-8 h-8 animate-spin" /></div>}
      <div className="absolute inset-x-0 bottom-0 px-3 py-2 text-xs font-medium text-white bg-black/60 flex items-center gap-2" data-testid={`${testid}-status`}>
        {status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Camera className="w-4 h-4" />} {msg}
      </div>
      <div className={`absolute inset-6 rounded-[40%] border-2 ${status === 'done' ? 'border-emerald-400' : status === 'blink' ? 'border-amber-300' : 'border-white/60'} pointer-events-none`} />
    </div>
  );
};

/** Enrol / re-enrol / remove the webcam face template (shown inside Face Login dialog + setup prompt). */
export const FaceEnrol = ({ onDone, compact = false }) => {
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [label, setLabel] = useState('');

  const load = useCallback(() => axios.get(`${API}/face/status`, cfg()).then((r) => setInfo(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const onCapture = useCallback(async ({ descriptors, liveness }) => {
    setBusy(true);
    try {
      await axios.post(`${API}/face/enroll`, { descriptors, liveness, label }, cfg());
      localStorage.setItem(FACE_DEVICE_KEY, '1');
      toast.success('Face scan enrolled — next time the camera signs you in');
      setScanning(false); load(); onDone?.();
    } catch (e) { toast.error(friendlyCamError(e)); setScanning(false); }
    finally { setBusy(false); }
  }, [label, load, onDone]);
  const onError = useCallback((e) => { toast.error(friendlyCamError(e)); setScanning(false); }, []);

  const remove = async () => {
    if (!window.confirm('Remove your enrolled face? Face scan login will stop working on all your devices.')) return;
    await axios.delete(`${API}/face/enroll`, cfg()).catch(() => {});
    localStorage.removeItem(FACE_DEVICE_KEY); load();
    toast.success('Face enrolment removed');
  };

  if (!cameraSupported()) return <p className="text-xs text-slate-500">This browser has no camera access — face scan login is not available here.</p>;
  return (
    <div className="space-y-2" data-testid="face-enrol">
      {!compact && info?.enrolled && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center justify-between" data-testid="face-enrol-status">
          <span><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Face enrolled ({info.samples} samples) · {String(info.enrolled_at || '').slice(0, 10)}</span>
          <button onClick={remove} className="text-red-600 hover:underline flex items-center gap-1" data-testid="face-enrol-remove"><Trash2 className="w-3 h-3" />Remove</button>
        </p>
      )}
      {scanning ? (
        <>
          <FaceCamera samples={3} onCapture={onCapture} onError={onError} testid="face-enrol-camera" />
          <Button variant="outline" className="w-full" onClick={() => setScanning(false)} disabled={busy} data-testid="face-enrol-cancel">Cancel</Button>
        </>
      ) : (
        <>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} data-testid="face-enrol-label"
            placeholder="Device name (e.g. Office MacBook) — optional"
            className="w-full h-9 px-3 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          <Button onClick={() => setScanning(true)} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="face-enrol-start">
            <Camera className="w-4 h-4 mr-2" /> {info?.enrolled ? 'Re-scan my face' : 'Scan my face with the camera'}
          </Button>
          <p className="text-[11px] text-slate-500">Takes ~5 seconds: look at the camera, then blink once. Only a mathematical face signature is stored (encrypted) — never a photo.</p>
        </>
      )}
    </div>
  );
};

/** Login page: camera sign-in for enrolled accounts on a trusted browser. */
export const FaceLoginCard = ({ onSuccess, auto = false, accent = 'teal' }) => {
  const [device, setDevice] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const autoRef = useRef(false);
  const [skipAuto] = useState(() => !!sessionStorage.getItem('jmepl_skip_auto_face'));

  useEffect(() => {
    if (skipAuto) sessionStorage.removeItem('jmepl_skip_auto_face');
    axios.get(`${API}/face/device`, cfg()).then((r) => {
      setDevice(r.data);
      if (auto && !skipAuto && !autoRef.current && r.data.trusted && r.data.accounts.length) {
        autoRef.current = true;
        setTimeout(() => setScanning(true), 400);
      }
    }).catch(() => setDevice({ trusted: false, accounts: [] }));
  }, [auto, skipAuto]);

  const onCapture = useCallback(async ({ descriptors, liveness }) => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/face/login`, { descriptor: descriptors[0], liveness }, cfg());
      setScanning(false);
      onSuccess(res.data);
    } catch (e) {
      setError(friendlyCamError(e)); setScanning(false);
    } finally { setBusy(false); }
  }, [onSuccess]);
  const onError = useCallback((e) => { setError(friendlyCamError(e)); setScanning(false); }, []);

  if (!cameraSupported() || !device?.trusted || !device.accounts.length) return null;
  const btn = accent === 'orange' ? 'border-orange-500 text-orange-700 hover:bg-orange-500 hover:text-white' : 'border-slate-800 text-slate-900 hover:bg-slate-900 hover:text-white';
  return (
    <div className="w-full space-y-2" data-testid="face-login-card">
      {scanning ? (
        <>
          <FaceCamera samples={1} onCapture={onCapture} onError={onError} testid="face-login-camera" />
          <Button type="button" variant="outline" className="w-full" onClick={() => setScanning(false)} disabled={busy} data-testid="face-login-cancel">Cancel</Button>
        </>
      ) : (
        <Button type="button" variant="outline" onClick={() => { setError(''); setScanning(true); }} data-testid="face-login-btn"
          className={`w-full h-11 border-2 font-semibold ${btn}`}>
          {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ScanFace className="w-5 h-5 mr-2" />} Sign in with Face Scan (camera)
        </Button>
      )}
      <p className="text-[11px] text-center opacity-80" data-testid="face-login-accounts">Enrolled on this device: {device.accounts.map((a) => a.name).join(', ')}</p>
      {error && <p className="text-xs text-center text-red-600" data-testid="face-login-error">{error}</p>}
    </div>
  );
};

/** After password/passkey login: mark this browser trusted so face scan works here. */
export const trustThisDevice = () => axios.post(`${API}/face/trust-device`, {}, cfg()).catch(() => {});
export const faceDeviceReady = () => !!localStorage.getItem(FACE_DEVICE_KEY);

/** Post-login prompt for devices without Face ID / Touch ID / Windows Hello. */
export const FaceSetupDialog = ({ open, onClose, portal = false }) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(false); }}>
    <DialogContent className="max-w-sm" data-testid="face-setup-prompt">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Camera className={`w-5 h-5 ${portal ? 'text-orange-500' : 'text-teal-600'}`} /> Sign in with your camera next time?</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-slate-600">Scan your face once with this device's camera. Next time you open the app, the camera recognises you and signs you in — no password.</p>
      <FaceEnrol compact onDone={() => onClose(true)} />
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => onClose(false)} data-testid="face-prompt-later-btn">Later</Button>
        <Button variant="ghost" className="flex-1 text-slate-500" onClick={() => onClose('snooze')} data-testid="face-prompt-never-btn">Ask me in 7 days</Button>
      </div>
    </DialogContent>
  </Dialog>
);
